// frontend/src/components/ContactMessagesAccordian.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-15
// Updated:      2026-09-10 10:31 BST
//
// Description:
//   Accordion showing messaging history *for a single contact*.
//   • New message row with Email / SMS / WhatsApp icons (wired to parent).
//   • Loads history via contact-scoped conversations/messages endpoint.
//   • Uses /api/conversations/contact/{contact_id}/messages (MessageRead).
//   • Includes filter dropdown for All / Open / Closed conversations (where available).
//   • Each history row is clickable and opens the channel-specific modal
//     with the thread loaded (via onNewEmail / onNewSms / onNewWhatsApp).
//
// Mobile Responsiveness (2026-09-10):
//   • Preserves the contact-scoped messaging endpoint, normalisation, status filtering,
//     row-click thread payloads and new-message callbacks.
//   • Existing accordion structure remains unchanged.
//   • New-message controls stack cleanly and use touch-safe channel buttons on phones.
//   • Message-history heading/filter stack on narrow screens.
//   • Desktop retains the existing history table.
//   • Mobile transforms the SAME table rows into labelled message cards; no duplicate
//     message data tree is mounted.
//   • Mobile message history shows one complete message card per internal vertical
//     scroll/snap.
//   • Long addresses, subjects, previews and status values wrap safely on mobile.
//   • This component sizes only its own mobile history viewport and does not mutate
//     ancestor widget/page geometry.
//
// Operational Notes:
//   • /api/conversations/contact/{contact_id}/messages remains the only history source.
//   • Existing MessageRead normalisation, newest-first sorting and filter semantics are unchanged.
//   • Existing onNewEmail / onNewSms / onNewWhatsApp callback payloads are unchanged.
//   • Thread row clicks continue to pass contactId, conversationId, messageId, channel
//     and mode: "thread" to the existing parent callbacks.

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { FaEnvelope, FaSms, FaWhatsapp } from "react-icons/fa";

const extractHttpError = (e) => {
  const r = e?.response;
  if (!r) return e?.message || String(e);
  const d = r.data;
  const detail =
    (d && (typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail))) ||
    (typeof d === "string" ? d : JSON.stringify(d));
  return `${r.status} ${r.statusText || ""} - ${detail}`.trim();
};

// Basic HTML tag stripper for body_html fallbacks
const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]+>/g, " ");
};

const ContactMessagesAccordian = ({
  contactId,
  defaultOpen = false,
  onNewEmail,
  onNewSms,
  onNewWhatsApp,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const historyViewportRef = useRef(null);
  const firstMessageRowRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | closed

  // Helpful warning if nothing is wired from the parent
  useEffect(() => {
    if (!onNewEmail && !onNewSms && !onNewWhatsApp) {
      console.warn(
        "[ContactMessagesAccordian] No modal handlers supplied; " +
          "channel buttons and row clicks will not open any modals."
      );
    }
  }, [onNewEmail, onNewSms, onNewWhatsApp]);

  useEffect(() => {
    if (!open || !contactId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErr("");

      try {
        // 🔑 Contact-scoped messages endpoint:
        // backend: /api/conversations/contact/{contact_id}/messages
        const { data } = await axiosInstance.get(
          `conversations/contact/${contactId}/messages`
        );

        if (cancelled) return;

        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];

        if (rows.length) {
          console.info(
            "[ContactMessagesAccordian] raw payload from conversations/contact/messages for contact",
            contactId,
            "→",
            rows.slice(0, 10)
          );
        } else {
          console.info(
            "[ContactMessagesAccordian] EMPTY payload from conversations/contact/messages for contact",
            contactId
          );
        }

        // Normalise MessageRead rows (from messages table)
        const normalised = rows
          .map((m) => {
            // MessageRead fields we expect from backend:
            // id, conversation_id, customer_id, contact_id, channel, subject,
            // body_text, body_html, delivery_status, direction,
            // metadata_json, created_at, ...

            const channel = m.channel || "unknown";

            let direction =
              m.direction && typeof m.direction === "string"
                ? m.direction.toLowerCase()
                : m.direction || "unknown";

            // Try to pull from / to from metadata_json where available
            let to_display = "";
            let from_display = "";

            const meta =
              m.metadata_json && typeof m.metadata_json === "object"
                ? m.metadata_json
                : null;

            if (meta) {
              to_display =
                meta.to ||
                meta.to_email ||
                meta.to_address ||
                meta.to_phone ||
                meta.to_phone_normalised ||
                "";
              from_display =
                meta.from ||
                meta.from_email ||
                meta.from_address ||
                meta.from_number ||
                "";
            }

            const subject = m.subject || "";

            const previewCandidate =
              m.preview ||
              m.body_text ||
              stripHtml(m.body_html) ||
              "";

            const preview = previewCandidate
              ? String(previewCandidate).slice(0, 200)
              : "";

            const created_at =
              m.created_at ||
              m.sent_at ||
              m.received_at ||
              m.last_message_at ||
              null;

            const delivery_status = m.delivery_status || "";

            // conversation_status isn't present on MessageRead; keep it empty for now
            const conversation_status = "";

            const status = delivery_status || conversation_status || "";

            // ✅ Ensure we always keep a contact_id on the row itself
            const effectiveContactId = m.contact_id ?? contactId ?? null;

            return {
              id: m.id,
              conversation_id: m.conversation_id ?? null,
              contact_id: effectiveContactId,
              channel,
              direction,
              subject,
              to_display,
              from_display,
              preview,
              created_at,
              status,
              conversation_status,
            };
          })
          .sort((a, b) => {
            const da = a.created_at ? new Date(a.created_at).getTime() : 0;
            const db = b.created_at ? new Date(b.created_at).getTime() : 0;
            // newest first (descending)
            return db - da;
          });

        console.info(
          "[ContactMessagesAccordian] normalised messages for contact",
          contactId,
          "→",
          normalised
        );

        setMessages(normalised);
      } catch (e) {
        if (!cancelled) {
          setErr(extractHttpError(e));
          setMessages([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, contactId]);

  const formatDateGB = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const directionBadgeClass = (dir) => {
    const d = String(dir || "").toLowerCase();
    if (d === "inbound") {
      return "bg-green-100 text-green-700 border border-green-300";
    }
    if (d === "outbound") {
      return "bg-blue-100 text-blue-700 border border-blue-300";
    }
    return "bg-gray-100 text-gray-600 border border-gray-300";
  };

  const channelLabel = (ch) => {
    const c = String(ch || "").toLowerCase();
    if (c === "email") return "Email";
    if (c === "sms") return "SMS";
    if (c === "whatsapp") return "WhatsApp";
    return ch || "Unknown";
  };

  const filteredMessages =
    statusFilter === "all"
      ? messages
      : messages.filter((m) => {
          const cs = (m.conversation_status || m.status || "").toLowerCase();
          if (statusFilter === "open") return cs === "open";
          if (statusFilter === "closed") return cs === "closed";
          return true;
        });

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const viewport = historyViewportRef.current;
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    if (!viewport) {
      return undefined;
    }

    let animationFrame = 0;

    const applyViewportHeight = () => {
      cancelAnimationFrame(animationFrame);

      viewport.style.height = "";
      viewport.style.minHeight = "";

      if (!mediaQuery.matches || !open || loading) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const firstRow = firstMessageRowRef.current;

        if (!firstRow) {
          return;
        }

        const rowHeight = Math.ceil(
          Math.max(
            firstRow.getBoundingClientRect().height || 0,
            firstRow.scrollHeight || 0
          )
        );

        if (!Number.isFinite(rowHeight) || rowHeight <= 0) {
          return;
        }

        const viewportHeight = rowHeight + 10;
        viewport.style.height = `${viewportHeight}px`;
        viewport.style.minHeight = `${viewportHeight}px`;
      });
    };

    applyViewportHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyViewportHeight())
        : null;

    if (resizeObserver && firstMessageRowRef.current) {
      resizeObserver.observe(firstMessageRowRef.current);
    }

    const handleMediaChange = () => applyViewportHeight();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyViewportHeight);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyViewportHeight);

      viewport.style.height = "";
      viewport.style.minHeight = "";
    };
  }, [
    filteredMessages.length,
    loading,
    open,
    statusFilter,
  ]);

  // 🔗 Row click → open appropriate modal (thread mode)
  const handleRowClick = (m) => {
    const channel = String(m.channel || "").toLowerCase();

    // ✅ Use row-level contact_id with fallback to prop
    const effectiveContactId = m.contact_id ?? contactId ?? null;
    const conversationId = m.conversation_id ?? null;
    const messageId = m.id ?? null;

    const payload = {
      contactId: effectiveContactId,
      conversationId,
      messageId,
      channel,
      mode: "thread",
    };

    // Extra debug so we can see exactly what reaches the parent
    console.info(
      "[ContactMessagesAccordian] handleRowClick payload",
      payload,
      "raw message",
      m
    );

    if (channel === "email" && onNewEmail) {
      onNewEmail(payload);
    } else if (channel === "sms" && onNewSms) {
      onNewSms(payload);
    } else if (channel === "whatsapp" && onNewWhatsApp) {
      onNewWhatsApp(payload);
    } else {
      // Fallback: if we ever add more channels in future, log them
      console.info(
        "[ContactMessagesAccordian] Row clicked with unsupported channel",
        payload
      );
    }
  };

  return (
    <div className="vx-contact-messages-accordion overflow-hidden rounded-xl border border-gray-200">
      <style>{`
        @media (max-width: 767px) {
          .vx-contact-messages-history {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-contact-messages-history::-webkit-scrollbar {
            display: none;
          }

          .vx-contact-messages-table,
          .vx-contact-messages-table tbody {
            display: block;
            width: 100%;
            min-width: 0;
          }

          .vx-contact-messages-table thead {
            display: none;
          }

          .vx-contact-messages-table tr.vx-contact-message-row {
            display: grid;
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            margin: 0 0 8px 0;
            padding: 10px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            background: #fff;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-contact-messages-table tr.vx-contact-message-row:last-child {
            margin-bottom: 0;
          }

          .vx-contact-messages-table tr.vx-contact-message-row td {
            display: grid;
            grid-template-columns: minmax(100px, 0.75fr) minmax(0, 1.25fr);
            gap: 10px;
            align-items: start;
            min-width: 0;
            padding: 7px 0 !important;
            border-bottom: 1px solid #f1f5f9;
            white-space: normal !important;
            text-align: left !important;
            overflow-wrap: anywhere;
          }

          .vx-contact-messages-table tr.vx-contact-message-row td:last-child {
            border-bottom: 0;
          }

          .vx-contact-messages-table tr.vx-contact-message-row td::before {
            content: attr(data-label);
            min-width: 0;
            font-size: 12px;
            font-weight: 700;
            color: #6b7280;
            overflow-wrap: anywhere;
          }

          .vx-contact-messages-table tr.vx-contact-message-state-row {
            display: table-row;
          }

          .vx-contact-messages-table tr.vx-contact-message-state-row td {
            display: table-cell;
            width: 100%;
            padding: 12px !important;
          }

          .vx-contact-messages-table tr.vx-contact-message-state-row td::before {
            content: none;
          }

          .vx-contact-message-preview {
            max-width: none !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }
        }
      `}</style>
      {/* Header */}
      <button
        type="button"
        className="flex min-h-[48px] w-full touch-manipulation items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold bg-btnGeneralNormal text-white"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        aria-controls="contact-messages-panel"
      >
        <span className="min-w-0 break-words">Contact Messages</span>
        <span className="ml-2 flex-none" aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div
          id="contact-messages-panel"
          className="space-y-4 border-t border-gray-200 bg-white p-3 sm:p-4"
        >
          {/* New message row */}
          <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">New message</div>
              <div className="break-words text-xs text-gray-600">
                Choose a channel to send a new message to this contact.
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xl sm:flex sm:items-center sm:gap-3">
              <button
                type="button"
                onClick={() =>
                  onNewEmail?.({
                    contactId,
                    channel: "email",
                    mode: "new",
                  })
                }
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnActionNormal text-white shadow hover:bg-btnActionHover active:bg-btnActionClick"
                title="New Email"
              >
                <FaEnvelope />
              </button>
              <button
                type="button"
                onClick={() =>
                  onNewSms?.({
                    contactId,
                    channel: "sms",
                    mode: "new",
                  })
                }
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnGeneralNormal text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                title="New SMS"
              >
                <FaSms />
              </button>
              <button
                type="button"
                onClick={() =>
                  onNewWhatsApp?.({
                    contactId,
                    channel: "whatsapp",
                    mode: "new",
                  })
                }
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-green-600 text-white shadow hover:bg-green-700 active:bg-green-800"
                title="New WhatsApp"
              >
                <FaWhatsapp />
              </button>
            </div>
          </div>

          {/* Errors */}
          {err && (
            <div className="break-words rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              ❌ {err}
            </div>
          )}

          {/* History table */}
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="flex flex-col gap-2 border-b border-gray-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-2">
              <div className="min-w-0 break-words text-sm font-semibold">
                Message history for this contact
              </div>
              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2 text-xs text-gray-700 sm:flex">
                <span>Filter:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="min-h-[44px] min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-xs"
                >
                  <option value="all">All</option>
                  <option value="open">Open only</option>
                  <option value="closed">Closed only</option>
                </select>
              </div>
            </div>
            <div ref={historyViewportRef} className="vx-contact-messages-history overflow-auto">
              <table className="vx-contact-messages-table w-full text-left text-sm text-gray-700">
                <thead>
                  <tr className="bg-white">
                    <th className="px-3 py-2 border-b">Date</th>
                    <th className="px-3 py-2 border-b">Channel</th>
                    <th className="px-3 py-2 border-b">Direction</th>
                    <th className="px-3 py-2 border-b">From / To</th>
                    <th className="px-3 py-2 border-b">Subject / Preview</th>
                    <th className="px-3 py-2 border-b">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr className="vx-contact-message-state-row">
                      <td
                        colSpan={6}
                        className="px-3 py-3 text-gray-500 italic"
                      >
                        Loading messages…
                      </td>
                    </tr>
                  ) : filteredMessages.length === 0 ? (
                    <tr className="vx-contact-message-state-row">
                      <td
                        colSpan={6}
                        className="px-3 py-3 text-gray-500 italic"
                      >
                        No messages recorded for this contact yet.
                      </td>
                    </tr>
                  ) : (
                    filteredMessages.map((m, index) => (
                      <tr
                        key={m.id}
                        ref={index === 0 ? firstMessageRowRef : null}
                        className="vx-contact-message-row cursor-pointer odd:bg-gray-50 hover:bg-blue-50"
                        onClick={() => handleRowClick(m)}
                      >
                        <td data-label="Date" className="px-3 py-2 whitespace-nowrap">
                          {formatDateGB(m.created_at)}
                        </td>
                        <td data-label="Channel" className="px-3 py-2 whitespace-nowrap">
                          {channelLabel(m.channel)}
                        </td>
                        <td data-label="Direction" className="px-3 py-2 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${directionBadgeClass(
                              m.direction
                            )}`}
                          >
                            {m.direction || "unknown"}
                          </span>
                        </td>
                        <td data-label="From / To" className="px-3 py-2">
                          <div className="break-words text-xs text-gray-700">
                            {m.direction === "outbound" ? (
                              <>
                                <span className="font-semibold">To:</span>{" "}
                                {m.to_display || "-"}
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">From:</span>{" "}
                                {m.from_display || "-"}
                              </>
                            )}
                          </div>
                        </td>
                        <td data-label="Subject / Preview" className="px-3 py-2">
                          <div className="break-words text-xs font-semibold text-gray-800">
                            {m.subject || "(no subject)"}
                          </div>
                          {m.preview && (
                            <div className="vx-contact-message-preview break-words text-xs text-gray-600 md:max-w-xs md:truncate">
                              {m.preview}
                            </div>
                          )}
                        </td>
                        <td data-label="Status" className="px-3 py-2 whitespace-nowrap">
                          <span className="break-words text-xs text-gray-700">
                            {m.status || "-"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessagesAccordian;
